import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { getWalletActivity, type ActivityItem } from '../../api/wallet'

export default function ActivityList() {
  const [items, setItems] = useState<ActivityItem[]>([])
  const [next, setNext] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setLoading(true)
    getWalletActivity()
      .then((p) => { setItems(p.items || []); setNext(p.next || null) })
      .catch((e) => setError(e?.message || 'Failed to load activity'))
      .finally(()=> setLoading(false))
  }, [])

  async function loadMore() {
    if (!next || loading) return
    setLoading(true)
    try {
      const p = await getWalletActivity(next)
      setItems((prev) => prev.concat(p.items || []))
      setNext(p.next || null)
    } catch (e: any) {
      setError(e?.message || 'Failed to load more')
    } finally {
      setLoading(false)
    }
  }

  if (loading && items.length === 0) return <div className="text-white/70">Loading…</div>
  if (error && items.length === 0) return <div className="text-red-400 text-sm">{error}</div>

  function receiptEntryId(it: ActivityItem): number | null {
    const kind = String(it.kind || '').toLowerCase()
    const isTransfer = kind === 'send' || kind === 'receive'
    if (!isTransfer) return null
    if (typeof it.journal_entry_id === 'number') return it.journal_entry_id
    const parsed = Number(it.id)
    return Number.isFinite(parsed) ? parsed : null
  }

  return (
    <div className="rounded-xl border border-white/10 bg-white/5 overflow-x-auto">
      <div className="min-w-[760px]">
        <div className="grid grid-cols-[1.2fr_1fr_1.2fr_0.8fr] px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white/60 border-b border-white/10">
          <div>Title</div>
          <div>Date</div>
          <div>Description</div>
          <div className="text-right">Amount</div>
        </div>
        {items.length === 0 ? (
          <div className="p-4 text-sm text-white/70">No activity yet.</div>
        ) : (
          items.map((it) => (
            <div key={it.id} className="grid grid-cols-[1.2fr_1fr_1.2fr_0.8fr] px-4 py-3 border-b border-white/10 gap-3">
              <div className="min-w-0">
                <div className="text-white font-semibold truncate">{it.title}</div>
                {receiptEntryId(it) ? (
                  <div className="mt-1">
                    <Link
                      to={`/wallet/receipts/${receiptEntryId(it)}`}
                      className="inline-flex items-center rounded-md border border-white/20 px-2 py-0.5 text-xs text-white/85 hover:bg-white/10"
                    >
                      Receipt
                    </Link>
                  </div>
                ) : null}
              </div>
              <div className="text-xs text-white/60">{new Date(it.ts).toLocaleString()}</div>
              <div className="text-sm text-white/70 truncate">{it.description || '—'}</div>
              <div className="text-right">
                <div className={`font-semibold ${it.amount >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
                  {it.amount >= 0 ? '+' : ''}
                  {it.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} PIXE
                </div>
                {typeof it.fiat === 'number' && (
                  <div className="text-xs text-white/70">
                    {new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(it.fiat || 0)}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
        {next && (
          <div className="p-3 text-right">
            <button onClick={loadMore} disabled={loading} className="px-3 py-1.5 rounded-lg border border-white/15 text-white/90 hover:bg-white/10 disabled:opacity-60">{loading ? 'Loading…' : 'Load more'}</button>
          </div>
        )}
      </div>
    </div>
  )
}
